// Read-only "receipt" of the user's current selections, shown in the Send
// Inquiry modal so they can verify exactly what they're requesting before
// submitting. Mirrors the customer confirmation email: products grouped under
// category headings, each with its selected options as sub-items, and any
// auto-included products nested beneath their parent, marked "(included)".
// Items priced manually are tagged "(custom quote)".
//
// Purely informational — no editing here (the user cancels to change a
// selection). Kept visually quiet (a tinted block, typographic hierarchy, no
// chips) so it doesn't compete with the Additional details field or Send button.
import { Box, Typography, Divider } from '@mui/material';
import { materialSymbolSx } from '../../theme.js';

const CUSTOM_QUOTE_TAG = ' (custom quote)';

const optionListText = (opts) => (
  opts.length ? ` — ${opts.map((o) => o.name).join(', ')}` : ''
);

const IncludedItem = ({ item }) => (
  <Box
    component="li"
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25,
    }}
  >
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        ...materialSymbolSx, fontSize: 16, color: 'text.disabled', mt: '2px',
      }}
    >
      subdirectory_arrow_right
    </Box>
    <Typography variant="body2" color="text.secondary">
      {`${item.name}${optionListText(item.options)} (included)`}
    </Typography>
  </Box>
);

const ProductItem = ({ product }) => {
  const hasChildren = product.options.length > 0 || product.included.length > 0;
  return (
    <Box component="li" sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            ...materialSymbolSx, fontSize: 18, color: 'primary.main', mt: '2px',
          }}
        >
          task_alt
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {product.name}
          {product.customQuote && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {CUSTOM_QUOTE_TAG}
            </Box>
          )}
        </Typography>
      </Box>

      {hasChildren && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, pl: 3.25 }}>
          {product.options.map((opt) => (
            <Typography key={opt.optionId} component="li" variant="body2" color="text.secondary">
              {`${opt.name}${opt.customQuote ? CUSTOM_QUOTE_TAG : ''}`}
            </Typography>
          ))}
          {product.included.map((inc) => (
            <IncludedItem key={`inc-${inc.productId}`} item={inc} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const SelectionSummaryList = ({ groups }) => {
  if (!groups.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No products selected yet.
      </Typography>
    );
  }

  return (
    <Box
      component="section"
      aria-label="Your selections"
      sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}
    >
      <Typography
        variant="overline"
        component="h3"
        color="text.secondary"
        sx={{ fontWeight: 700, display: 'block', lineHeight: 1.6 }}
      >
        Your selections
      </Typography>

      {groups.map((group, index) => (
        <Box key={group.categoryName} sx={{ mt: index === 0 ? 0.5 : 0 }}>
          {index > 0 && <Divider sx={{ my: 1.5 }} />}
          <Typography
            variant="overline"
            component="h4"
            color="text.secondary"
            sx={{
              fontWeight: 600, display: 'block', letterSpacing: 0.6, lineHeight: 1.6,
            }}
          >
            {group.categoryName}
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {group.products.map((product) => (
              <ProductItem key={product.productId} product={product} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SelectionSummaryList;
